/**
 * CommonDatePicker – date range filter matching admin4.0 CommonDatePicker.
 * Props: FromDate, ToDate, DateIndex, SelectedDates(from, to, index), HideAllMenu, HideTodayMenu, height, HideDate, LabelText, disable.
 */
import React, { useState, useEffect } from "react";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from "@mui/material";
import { DateTime } from "luxon";

const DATE_OPTIONS = [
  { value: 0, label: "All" },
  { value: 1, label: "Today" },
  { value: 2, label: "Yesterday" },
  { value: 3, label: "Last week" },
  { value: 4, label: "Last month" },
  { value: 5, label: "Custom" },
];

export default function CommonDatePicker({
  FromDate = "",
  ToDate = "",
  DateIndex = 0,
  SelectedDates,
  HideAllMenu = false,
  HideTodayMenu = false,
  height = false,
  HideDate = false,
  LabelText = "Select Date",
  disable = false,
  removeMarginRight = false,
}) {
  const [dateFilter, setDateFilter] = useState(DateIndex ?? 0);
  const [QueryFromDate, setQueryFromDate] = useState(FromDate ?? "");
  const [QueryToDate, setQueryToDate] = useState(ToDate ?? "");
  const [datePickerPop, setDatePickerPop] = useState(false);
  const [customFrom, setCustomFrom] = useState(FromDate || DateTime.now().toFormat("yyyy-MM-dd"));
  const [customTo, setCustomTo] = useState(ToDate || DateTime.now().toFormat("yyyy-MM-dd"));

  useEffect(() => {
    setDateFilter(DateIndex ?? 0);
    setQueryFromDate(FromDate ?? "");
    setQueryToDate(ToDate ?? "");
    setCustomFrom(FromDate || DateTime.now().toFormat("yyyy-MM-dd"));
    setCustomTo(ToDate || DateTime.now().toFormat("yyyy-MM-dd"));
  }, [DateIndex, FromDate, ToDate]);

  function getDateHelperText() {
    if (dateFilter === 0) return "";
    if (dateFilter === 1 && !HideTodayMenu) return DateTime.now().toFormat("dd-MM-yyyy");
    if (dateFilter === 2 || (HideTodayMenu && dateFilter === 1))
      return DateTime.now().minus({ days: 1 }).toFormat("dd-MM-yyyy");
    if (dateFilter === 3) {
      const start = DateTime.now().minus({ weeks: 1 }).startOf("week").minus({ days: 1 });
      const end = DateTime.now().minus({ weeks: 1 }).endOf("week").minus({ days: 1 });
      return `${start.toFormat("dd-MM-yyyy")} to ${end.toFormat("dd-MM-yyyy")}`;
    }
    if (dateFilter === 4) {
      const start = DateTime.now().minus({ months: 1 }).startOf("month");
      const end = DateTime.now().minus({ months: 1 }).endOf("month");
      return `${start.toFormat("dd-MM-yyyy")} to ${end.toFormat("dd-MM-yyyy")}`;
    }
    if (dateFilter === 5 && QueryFromDate && QueryToDate) {
      const from = QueryFromDate.split("-").reverse().join("-");
      const to = QueryToDate.split("-").reverse().join("-");
      return `${from} to ${to}`;
    }
    return "";
  }

  function handleDateChange(value) {
    setDateFilter(value);
    const today = DateTime.now().toFormat("yyyy-MM-dd");
    const yesterday = DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd");
    const startOfPreviousWeek = DateTime.now().minus({ weeks: 1 }).startOf("week").minus({ days: 1 }).toFormat("yyyy-MM-dd");
    const endOfPreviousWeek = DateTime.now().minus({ weeks: 1 }).endOf("week").minus({ days: 1 }).toFormat("yyyy-MM-dd");
    const startOfPreviousMonth = DateTime.now().minus({ months: 1 }).startOf("month").toFormat("yyyy-MM-dd");
    const endOfPreviousMonth = DateTime.now().minus({ months: 1 }).endOf("month").toFormat("yyyy-MM-dd");

    if (value === 0) {
      setQueryFromDate("");
      setQueryToDate("");
      SelectedDates?.("", "", value);
      return;
    }
    if (value === 1) {
      const from = HideTodayMenu ? yesterday : today;
      const to = HideTodayMenu ? yesterday : today;
      setQueryFromDate(from);
      setQueryToDate(to);
      SelectedDates?.(from, to, value);
      return;
    }
    if (value === 2) {
      setQueryFromDate(yesterday);
      setQueryToDate(yesterday);
      SelectedDates?.(yesterday, yesterday, value);
      return;
    }
    if (value === 3) {
      setQueryFromDate(startOfPreviousWeek);
      setQueryToDate(endOfPreviousWeek);
      SelectedDates?.(startOfPreviousWeek, endOfPreviousWeek, value);
      return;
    }
    if (value === 4) {
      setQueryFromDate(startOfPreviousMonth);
      setQueryToDate(endOfPreviousMonth);
      SelectedDates?.(startOfPreviousMonth, endOfPreviousMonth, value);
      return;
    }
    if (value === 5) {
      setDatePickerPop(true);
    }
  }

  function handleCustomApply() {
    setQueryFromDate(customFrom);
    setQueryToDate(customTo);
    setDatePickerPop(false);
    SelectedDates?.(customFrom, customTo, 5);
  }

  function handlePopClose() {
    setDatePickerPop(false);
  }

  const menuItems = DATE_OPTIONS.filter((opt) => {
    if (opt.value === 0 && HideAllMenu) return false;
    if (opt.value === 1 && HideTodayMenu) return false;
    return true;
  });

  return (
    <div>
      <FormControl fullWidth size="small">
        <InputLabel>{LabelText}</InputLabel>
        <Select
          label={LabelText}
          size="small"
          disabled={disable}
          onChange={(e) => handleDateChange(Number(e.target.value))}
          value={dateFilter}
          sx={{
            height: height ? "2.5vw" : HideDate ? "1.8vw" : "2.2vw",
            cursor: "pointer",
            marginRight: removeMarginRight ? 0 : "1vw",
          }}
        >
          {menuItems.map((opt) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              onClick={opt.value === 5 ? () => setDatePickerPop(true) : undefined}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {!HideDate && <FormHelperText id="eMed_form_helpertext">{getDateHelperText()}</FormHelperText>}
      </FormControl>

      <Dialog open={datePickerPop} onClose={handlePopClose} maxWidth="xs" fullWidth>
        <DialogTitle>Custom Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              type="date"
              size="small"
              label="From Date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              type="date"
              size="small"
              label="To Date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePopClose}>Cancel</Button>
          <Button variant="contained" onClick={handleCustomApply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
